import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Menu, X, LogOut, LayoutDashboard, Car, MapPin, Home, Zap } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getDashboardPath = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "driver") return "/driver";
    return "/passenger";
  };

  const isActive = (path) => location.pathname === path;

  const homePath = () => {
    if (!user) return "/";
    if (user.role === "admin") return "/admin";
    if (user.role === "driver") return "/driver";
    if (user.role === "partner") return "/partner";
    return "/passenger";
  };

  const navLinks = [
    { to: homePath(), label: "Accueil", icon: Home },
    { to: "/express", label: "Express", icon: Zap },
    { to: "/vehicles", label: "Location", icon: Car },
    { to: "/rides", label: "Covoiturage", icon: MapPin },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
            <img src="/trigo-logo.png" alt="TrigoTour" className="w-9 h-9 object-contain" />
            <span className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>TrigoTour</span>
          </Link>

          {/* Center nav links - desktop */}
          <div className="hidden md:flex items-center justify-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to}
                className={`flex flex-col items-center px-5 py-2 rounded-full text-xs font-medium transition-all ${isActive(to) ? "bg-[#F7F7F9] text-[#0A2540]" : "text-gray-500 hover:bg-[#F7F7F9] hover:text-[#0A2540]"}`}
                data-testid={`nav-${label.toLowerCase()}`}>
                <Icon className="w-4 h-4 mb-0.5" />
                {label}
              </Link>
            ))}
          </div>

          {/* Right actions - desktop */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {user ? (
              <>
                <Link to={getDashboardPath()}>
                  <Button size="sm" className="bg-[#0A2540] text-white rounded-full px-5 hover:bg-[#0f365c] text-xs h-9" data-testid="nav-dashboard-btn">
                    <LayoutDashboard className="w-3.5 h-3.5 mr-1.5" /> Mon espace
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-[#EF4444] rounded-full h-9" data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-[#0A2540] rounded-full px-4 h-9 text-sm font-medium" data-testid="nav-login-btn">Connexion</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-[#FACC15] text-[#0A2540] hover:bg-[#eab308] font-semibold rounded-full px-5 h-9 text-sm" data-testid="nav-register-btn">Inscription</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden p-2 rounded-full hover:bg-[#F7F7F9] transition-colors" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="w-5 h-5 text-[#0A2540]" /> : <Menu className="w-5 h-5 text-[#0A2540]" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-6 pt-3 space-y-1 animate-in slide-in-from-top-2">
          {navLinks.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive(to) ? "bg-[#F7F7F9] text-[#0A2540]" : "text-gray-500 hover:bg-[#F7F7F9]"}`}>
              <Icon className="w-4 h-4" /> {label}
            </Link>
          ))}
          <div className="border-t border-gray-100 my-2 pt-2" />
          {user ? (
            <>
              <Link to={getDashboardPath()} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#0A2540] hover:bg-[#F7F7F9]">
                <LayoutDashboard className="w-4 h-4" /> Mon espace
              </Link>
              <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#EF4444] hover:bg-red-50 w-full text-left">
                <LogOut className="w-4 h-4" /> Deconnexion
              </button>
            </>
          ) : (
            <div className="flex gap-2 px-2 pt-2">
              <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1">
                <Button variant="outline" className="w-full rounded-full border-gray-200 text-sm">Connexion</Button>
              </Link>
              <Link to="/register" onClick={() => setMenuOpen(false)} className="flex-1">
                <Button className="w-full rounded-full bg-[#FACC15] text-[#0A2540] font-semibold text-sm hover:bg-[#eab308]">Inscription</Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
